import { createSelector } from '@reduxjs/toolkit';

import { type TJsonQuery, getGeoValueByType } from '@/packages/JsonQuery';
import { type IJsonQueryGeo, type IJsonQueryPolygon, type SortType } from '@/packages/api-models/common/json_query';
import { hash } from '@/packages/utils/hash';

import { useReduxSelector } from '../../../hooks';
import { type MapBounds } from '../../mapConfiguration/slice';

import { jsonQuerySelector } from './root';

export const listingSortSelector = createSelector(jsonQuerySelector, state => state.sort);

export function useListingSortSelector(): SortType {
  return useReduxSelector(listingSortSelector);
}

export const filtersSelector = createSelector(jsonQuerySelector, state => state.filters);
export function useFiltersSelector(): TJsonQuery {
  return useReduxSelector(filtersSelector);
}

export const filtersBboxSelector = createSelector(filtersSelector, state => state.bbox?.value);
export function useFiltersBboxSelector(): MapBounds | undefined {
  return useReduxSelector(filtersBboxSelector);
}

export const pageSelector = createSelector(jsonQuerySelector, state => state.page);
export function usePageSelector(): number {
  return useReduxSelector(pageSelector);
}

export const needUpdateQueryKeySelector = createSelector(jsonQuerySelector, state => state.needUpdateQueryKey);
export function useNeedUpdateQueryKeySelector(): boolean {
  return useReduxSelector(needUpdateQueryKeySelector);
}

export const queryKeySelector = createSelector(jsonQuerySelector, state => state.queryKey);
export function useQueryKeySelector(): string[] {
  return useReduxSelector(queryKeySelector);
}

export const geoSelector = createSelector(filtersSelector, state => state.geo);
export function useGeoSelector(): IJsonQueryGeo | undefined {
  return useReduxSelector(geoSelector);
}

interface IPolygon {
  id: string;
  coordinates: YMaps.TCoord[];
  title?: string;
}

export const polygonsSelector = createSelector(filtersSelector, filters => {
  const jsonQueryPolygons = getGeoValueByType<IJsonQueryPolygon>('polygon')(filters);

  if (!jsonQueryPolygons) {
    return [];
  }

  return jsonQueryPolygons.map<IPolygon>(jsonQueryPolygon => {
    const { name, coordinates } = jsonQueryPolygon;
    const id = [jsonQueryPolygon.type, jsonQueryPolygon.name, String(jsonQueryPolygon.coordinates)]
      .map(hash)
      .reduce((acc, h) => acc ^ h, 0);

    return {
      id: String(id),
      title: name,
      coordinates: coordinates.map(([x, y]) => [Number(y), Number(x)]),
    };
  });
});

export function usePolygonsSelector(): IPolygon[] {
  return useReduxSelector(polygonsSelector);
}
